import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../shared/ThemedChartTooltip";
import InfoTip from "../../shared/InfoTip";
import { BRAND_BLUE } from "../../shared/colors";

/**
 * BandBarChart — shared evidence component for temperature_response and rain_elasticity.
 *
 * Vertical bars of conversion_rate_pct by band. Bands in array order (do not re-sort).
 * Bands with bookings < 30 are greyed out — excluded from the spread backend-side.
 * Tooltip shows sessions and bookings.
 *
 * Detects whichever band key is present (temp_band_f or precip_probability_band).
 *
 * Spec references: §7 (temperature_response, rain_elasticity).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

/** Find whichever band key is present in the data. */
const BAND_KEYS = ["temp_band_f", "precip_probability_band"];

const getBandKey = (bands) => {
  if (!bands || bands.length === 0) return BAND_KEYS[0];
  const first = bands[0];
  return BAND_KEYS.find((k) => first[k] != null) || BAND_KEYS[0];
};

/** Chart title based on panel key. */
const getChartTitle = (panelKey) => {
  if (panelKey === "rain_elasticity") return "Bookings by Rain Probability";
  return "Bookings by Temperature";
};

/** InfoTip text based on panel key. */
const getInfoTipText = (panelKey) => {
  if (panelKey === "rain_elasticity") {
    return "Conversion rate for each rain probability band. The spread shows how much conversion varies across wet vs dry forecasts. Greyed bands have too few bookings to be reliable.";
  }
  return "Conversion rate for each forecast high temperature band. The spread shows how much conversion varies across temperature bands. Greyed bands have too few bookings to be reliable.";
};

/** Unit suffix for band labels based on panel key. */
const bandSuffix = (panelKey) => {
  if (panelKey === "rain_elasticity") return "%";
  if (panelKey === "temperature_response") return "°F";
  return "";
};

/** Format a band label with its unit suffix.
 *  "60-74" → "60-74°F", "under 60" → "under 60°F", "95+" → "95°F+"
 *  "0-19" → "0-19%", "80-100" → "80-100%"
 */
const formatBandLabel = (label, panelKey) => {
  const suffix = bandSuffix(panelKey);
  if (!suffix || !label) return label;
  if (suffix === "°F" && label.endsWith("+")) {
    return `${label.slice(0, -1)}${suffix}+`;
  }
  return `${label}${suffix}`;
};

const BandBarChart = ({ panel }) => {
  const f = panel.facts || {};
  const bands = f.bands || [];

  if (bands.length === 0) return null;

  const bandKey = getBandKey(bands);
  const chartTitle = getChartTitle(panel.panel);
  const infoTipText = getInfoTipText(panel.panel);

  // Tag each band with its key so the tooltip can find the label
  const chartData = bands.map((b) => ({
    ...b,
    _bandKey: bandKey,
    // Use the raw value as the x-axis label (strings like "60-74", "0-19")
    label: b[bandKey],
    // Grey out bands with fewer than 30 bookings
    lowConfidence: (b.bookings || 0) < 30,
  }));

  const spread = f.conversion_spread_pct_points;

  return (
    <div className="space-y-4">
      {/* ─── Hero number ─────────────────────────────────────── */}
      <div className="flex items-end justify-between gap-4">
        <div className="flex items-baseline gap-2">
          {spread != null && (
            <>
              <span className="text-4xl font-extrabold leading-none text-gray-900 dark:text-gray-100">
                {spread}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                percentage point spread
              </span>
            </>
          )}
        </div>
      </div>

      {/* ─── Bar chart (in a card, like ExpectedVsActual) ──────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <span>{chartTitle}</span>
            <InfoTip text={infoTipText} />
          </h3>
          {f.window_days && f.sessions != null && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Last {f.window_days} days · {fmtNum(f.sessions)} visits
            </p>
          )}
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={{ stroke: "#374151" }}
              tickLine={false}
              tickFormatter={(v) => formatBandLabel(v, panel.panel)}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${v}%`}
              width={45}
            />
            <ThemedChartTooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const data = payload[0].payload;
                const bk = data._bandKey;
                return (
                  <ThemedTooltipBox title={formatBandLabel(data[bk], panel.panel)}>
                    <div>{fmtNum(data.sessions)} sessions · {fmtNum(data.bookings)} bookings</div>
                    <div>{data.conversion_rate_pct != null ? `${data.conversion_rate_pct}% conversion` : "—"}</div>
                  </ThemedTooltipBox>
                );
              }}
            />
            <ReferenceLine y={0} stroke="#374151" />
            <Bar dataKey="conversion_rate_pct" radius={[4, 4, 0, 0]} barSize={48}>
              {chartData.map((entry, index) => (
                <Cell
                  key={index}
                  fill={entry.lowConfidence ? "#D1D5DB" : BRAND_BLUE}
                  opacity={entry.lowConfidence ? 0.5 : 1}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Grey-out legend */}
        {chartData.some((d) => d.lowConfidence) && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-3 text-center">
            Greyed bands have fewer than 30 bookings and are excluded from the spread — they are not part of the trend, however many sessions they contain.
          </p>
        )}
      </div>
    </div>
  );
};

export default BandBarChart;